import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, AlertTriangle } from 'lucide-react';

const hoursNum = (h) => {
  const m = String(h || '').match(/(\d+(?:\.\d+)?)/);
  return m ? m[1] : '2';
};

export const JobDetailsEditor = ({ item, authFetch, onSaved }) => {
  const currentPrice = item.booking?.final_price ?? item.price ?? 0;
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(String(currentPrice));
  const [hours, setHours] = useState(hoursNum(item.hours_estimate));
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  const save = async () => {
    const p = parseInt(price, 10);
    const h = parseFloat(hours);
    if (Number.isNaN(p) || p < 0) return toast.error('Enter a valid price.');
    if (Number.isNaN(h) || h < 0.5 || h > 16) return toast.error('Enter hours between 0.5 and 16.');
    setSaving(true);
    try {
      const { data } = await authFetch('patch', `/estimate/${item.id}/job-details`, { price: p, hours: h });
      setConflicts(data.conflicts || []);
      if (data.conflicts?.length) {
        toast.warning(`Saved — but this now overlaps ${data.conflicts.map(c => `#${c.ref} at ${c.time}`).join(', ')}.`, { duration: 8000 });
      } else {
        toast.success('Price & hours updated.');
        setOpen(false);
      }
      onSaved?.(data.estimate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes.");
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--mv-orange)] h-11 px-3 -ml-3 rounded-md hover:bg-orange-50" data-testid={`job-edit-open-${item.ref}`}>
        <Pencil className="w-3.5 h-3.5" /> Edit hours / price
      </button>
    );
  }

  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 p-3 space-y-2" data-testid={`job-edit-panel-${item.ref}`}>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Job price (£)</span>
          <input type="number" min="0" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 w-full h-11 rounded border border-slate-200 bg-white px-2 text-base" data-testid="job-edit-price" />
        </label>
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">Hours</span>
          <input type="number" min="0.5" max="16" step="0.5" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1 w-full h-11 rounded border border-slate-200 bg-white px-2 text-base" data-testid="job-edit-hours" />
        </label>
      </div>
      {conflicts.length > 0 && (
        <div className="flex items-start gap-1.5 text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2" data-testid="job-edit-conflict-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>Overlaps with {conflicts.map(c => `#${c.ref} (${c.name || '—'}) at ${c.time}`).join(', ')}. Move one of the jobs to avoid a double booking.</span>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={() => { setOpen(false); setConflicts([]); }} className="text-sm font-bold text-slate-500 h-11 px-4" data-testid="job-edit-cancel">Close</button>
        <button type="button" onClick={save} disabled={saving} className="h-11 px-5 rounded-md bg-[color:var(--mv-navy)] text-white text-sm font-bold inline-flex items-center gap-1" data-testid="job-edit-save">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
};
